import { useEffect } from 'react'
import { GunIcon } from './gun-icon'
import { BoltIcon } from './bolt-icon'
import PropTypes from 'prop-types'

export const GunControls = ({
  gunMode,
  setGunMode,
  gunDefaultEvent,
  setGunDefaultEvent,
  gunAutoSubmit,
  setGunAutoSubmit,
  canAutoSubmitNow
}) => {
  const gunModeDisabled = !canAutoSubmitNow
  const gunModeTooltip = gunModeDisabled
    ? 'Seleccione una dependencia para activar Modo pistola'
    : gunMode
      ? 'Modo pistola activo'
      : 'Modo pistola'

  useEffect(() => {
    if (gunModeDisabled && gunMode) setGunMode(false)
  }, [gunModeDisabled, gunMode, setGunMode])

  useEffect(() => {
    if (!gunMode && gunAutoSubmit) setGunAutoSubmit(false)
  }, [gunMode, gunAutoSubmit, setGunAutoSubmit])

  useEffect(() => {
    if (!canAutoSubmitNow && gunAutoSubmit && (gunDefaultEvent === 'entry' || gunDefaultEvent === 'exit')) {
      setGunAutoSubmit(false)
    }
  }, [canAutoSubmitNow, gunAutoSubmit, gunDefaultEvent, setGunAutoSubmit])

  const cycleAutoState = () => {
    if (!gunMode) return
    const order = canAutoSubmitNow ? ['off', 'entry', 'exit'] : ['off']
    const current = gunAutoSubmit ? gunDefaultEvent : 'off'
    const currentIndex = order.includes(current) ? order.indexOf(current) : 0
    const next = order[(currentIndex + 1) % order.length]
    if (next === 'off') {
      setGunAutoSubmit(false)
    } else {
      setGunDefaultEvent(next)
      setGunAutoSubmit(true)
    }
  }

  const rawState = gunAutoSubmit && gunMode ? gunDefaultEvent : 'off'
  const autoState = !canAutoSubmitNow && (rawState === 'entry' || rawState === 'exit') ? 'off' : rawState

  const autoTooltip = !gunMode
    ? 'Activa Modo pistola para auto-enviar'
    : autoState === 'off'
      ? 'Auto-enviar al leer: desactivado'
      : `Auto-enviar al leer: ${autoState === 'entry' ? 'entrada' : 'salida'}`

  const actionColors = {
    off: { btn: 'bg-white text-gray-400 border-gray-200', icon: 'text-gray-300', caption: 'text-gray-500' },
    entry: { btn: 'bg-green-500 text-white border-green-600 shadow-inner', icon: 'text-white', caption: 'text-green-600' },
    exit: { btn: 'bg-red-500 text-white border-red-600 shadow-inner', icon: 'text-white', caption: 'text-red-600' }
  }

  const autoClasses = actionColors[autoState] || actionColors.off

  return (
    <div className="border border-gray-200 rounded p-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="relative group">
          <button
            type="button"
            onClick={(e) => {
              if (gunModeDisabled) return
              setGunMode(!gunMode)
              e.currentTarget.blur()
            }}
            aria-pressed={gunMode}
            aria-disabled={gunModeDisabled}
            aria-label={gunModeTooltip}
            disabled={gunModeDisabled}
            className={[
              'inline-flex items-center justify-center h-14 w-full rounded-xl border transition select-none',
              gunModeDisabled
                ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed'
                : gunMode
                  ? 'bg-red-600 text-white border-red-700 shadow-inner'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ].join(' ')}
          >
            <GunIcon className="h-7 w-7" />
          </button>
          <span
            className={[
              'pointer-events-none absolute left-1/2 -translate-x-1/2 mt-2 hidden whitespace-nowrap rounded-md px-2 py-1 text-xs text-white shadow group-hover:block',
              gunMode ? 'bg-red-700' : 'bg-gray-900'
            ].join(' ')}
            role="tooltip"
          >
            {gunModeTooltip}
          </span>
        </div>

        <div className="relative group">
          <button
            type="button"
            onClick={(e) => {
              cycleAutoState()
              e.currentTarget.blur()
            }}
            aria-pressed={gunAutoSubmit && gunMode}
            aria-disabled={!gunMode}
            disabled={!gunMode}
            aria-label={autoTooltip}
            className={[
              'inline-flex items-center justify-center h-14 w-full rounded-xl border transition select-none',
              gunMode ? autoClasses.btn : actionColors.off.btn,
              !gunMode ? 'cursor-not-allowed' : ''
            ].join(' ')}
          >
            <BoltIcon className={['h-7 w-7', gunMode ? autoClasses.icon : actionColors.off.icon].join(' ')} />
          </button>
          <span
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 mt-2 hidden whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white shadow group-hover:block"
            role="tooltip"
          >
            {autoTooltip}
          </span>
        </div>

        {gunMode && (
          <div className={`col-span-2 text-center text-xs mt-1 ${autoClasses.caption}`}>
            {autoState === 'off'
              ? 'Auto-envío desactivado'
              : `Auto-envío para ${autoState === 'entry' ? 'entrada' : 'salida'}`}
          </div>
        )}
      </div>
    </div>
  )
}

GunControls.propTypes = {
  gunMode: PropTypes.bool.isRequired,
  setGunMode: PropTypes.func.isRequired,
  gunDefaultEvent: PropTypes.string.isRequired,
  setGunDefaultEvent: PropTypes.func.isRequired,
  gunAutoSubmit: PropTypes.bool.isRequired,
  setGunAutoSubmit: PropTypes.func.isRequired,
  canAutoSubmitNow: PropTypes.bool.isRequired
}
